import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { API_BASE } from '../config/api';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${API_BASE}/api`;

/**
 * Netflix-style thumbs up/down feedback component
 * 
 * @param {string} contentType - Type of content: "course", "learning_lab", "masterclass", "external_module"
 * @param {string} contentId - ID of the content
 * @param {string} token - Auth token
 * @param {string} size - Size variant: "sm", "md", "lg"
 * @param {boolean} showStats - Whether to show approval stats
 * @param {string} className - Additional CSS classes
 */
export default function ContentFeedback({ 
  contentType, 
  contentId, 
  token,
  size = "md",
  showStats = false,
  className = ""
}) {
  const [userFeedback, setUserFeedback] = useState(null); // "up", "down", or null
  const [stats, setStats] = useState({ thumbs_up: 0, thumbs_down: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Size variants
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  const buttonSizeClasses = {
    sm: "p-1.5",
    md: "p-2",
    lg: "p-2.5"
  };

  const iconSize = sizeClasses[size] || sizeClasses.md;
  const buttonSize = buttonSizeClasses[size] || buttonSizeClasses.md;

  // Fetch user's existing feedback on mount
  useEffect(() => {
    if (contentId && token) {
      fetchFeedback();
    }
  }, [contentId, token]);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(
        `${API}/feedback/${contentType}/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setUserFeedback(response.data.user_feedback);
        setStats(response.data.stats);
      }
      setInitialized(true);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setInitialized(true);
    }
  };

  const handleFeedback = async (feedbackType) => {
    if (loading) return;
    
    setLoading(true);
    
    // Determine new feedback state
    const newFeedback = userFeedback === feedbackType ? "none" : feedbackType;
    
    try {
      const response = await axios.post(
        `${API}/feedback`,
        {
          content_type: contentType,
          content_id: contentId,
          feedback: newFeedback
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Optimistic UI update
        const oldFeedback = userFeedback;
        setUserFeedback(newFeedback === "none" ? null : newFeedback);
        
        // Update stats locally
        setStats(prev => {
          const newStats = { ...prev };
          
          // Remove old feedback from stats
          if (oldFeedback === "up") newStats.thumbs_up = Math.max(0, newStats.thumbs_up - 1);
          if (oldFeedback === "down") newStats.thumbs_down = Math.max(0, newStats.thumbs_down - 1);
          
          // Add new feedback to stats
          if (newFeedback === "up") newStats.thumbs_up += 1;
          if (newFeedback === "down") newStats.thumbs_down += 1;
          
          newStats.total = newStats.thumbs_up + newStats.thumbs_down;
          return newStats;
        });
        
        // Show toast only on new feedback, not removal
        if (newFeedback !== "none") {
          toast.success(newFeedback === "up" ? "Thanks for the feedback!" : "We'll work to improve this");
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className={cn("rounded-full bg-zinc-800 animate-pulse", buttonSize, "w-8 h-8")} />
        <div className={cn("rounded-full bg-zinc-800 animate-pulse", buttonSize, "w-8 h-8")} />
      </div>
    );
  }

  const approvalRate = stats.total > 0 
    ? Math.round((stats.thumbs_up / stats.total) * 100) 
    : 0;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Thumbs Up Button */}
      <button
        onClick={() => handleFeedback("up")}
        disabled={loading}
        className={cn(
          "rounded-full transition-all duration-200 border",
          buttonSize,
          userFeedback === "up"
            ? "bg-green-500/20 border-green-500 text-green-400 hover:bg-green-500/30"
            : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-white hover:border-zinc-600",
          loading && "opacity-50 cursor-not-allowed"
        )}
        title="I liked this"
        data-testid="feedback-thumbs-up"
      >
        <ThumbsUp 
          className={cn(
            iconSize,
            userFeedback === "up" && "fill-current"
          )} 
        />
      </button>

      {/* Thumbs Down Button */}
      <button
        onClick={() => handleFeedback("down")}
        disabled={loading}
        className={cn(
          "rounded-full transition-all duration-200 border",
          buttonSize,
          userFeedback === "down"
            ? "bg-red-500/20 border-red-500 text-red-400 hover:bg-red-500/30"
            : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-white hover:border-zinc-600",
          loading && "opacity-50 cursor-not-allowed"
        )}
        title="Not for me"
        data-testid="feedback-thumbs-down"
      >
        <ThumbsDown 
          className={cn(
            iconSize,
            userFeedback === "down" && "fill-current"
          )} 
        />
      </button>

      {/* Optional Stats Display */}
      {showStats && stats.total > 0 && (
        <div className="flex items-center gap-1.5 ml-2 text-xs text-zinc-500">
          <span className={cn(
            "font-medium",
            approvalRate >= 70 ? "text-green-400" : approvalRate >= 50 ? "text-yellow-400" : "text-zinc-400"
          )}>
            {approvalRate}% liked
          </span>
          <span>({stats.total})</span>
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for cards/lists
 */
export function ContentFeedbackCompact({ contentType, contentId, token, className }) {
  return (
    <ContentFeedback
      contentType={contentType}
      contentId={contentId}
      token={token}
      size="sm"
      showStats={false}
      className={className}
    />
  );
}

/**
 * Full version with stats for detail pages
 */
export function ContentFeedbackWithStats({ contentType, contentId, token, className }) {
  return (
    <ContentFeedback
      contentType={contentType}
      contentId={contentId}
      token={token}
      size="md"
      showStats={true}
      className={className}
    />
  );
}
